import {
  Box,
  Container,
  Typography,
  Button,
  Stack,
  useTheme
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight } from "@fortawesome/free-solid-svg-icons";

import { hexToRgba } from "@aclymatepackages/converters";

import Link from "../../../atoms/mui/Link";

const ContrastPrimaryButton = ({ cta, palette }) => (
  <Link href={cta.href}>
    <Button
      variant="contained"
      size="large"
      sx={{
        backgroundColor: palette.background.paper,
        color: palette.primary.main,
        "&:hover": {
          backgroundColor: hexToRgba(palette.background.paper, 0.9)
        }
      }}
    >
      {cta.label}
    </Button>
  </Link>
);

const ContrastSecondaryButton = ({ cta, palette }) => (
  <Link href={cta.href}>
    <Button
      variant="outlined"
      size="large"
      sx={{
        color: palette.background.paper,
        borderColor: hexToRgba(palette.background.paper, 0.7)
      }}
    >
      {cta.label}
    </Button>
  </Link>
);

const DefaultPrimaryButton = ({ cta }) => (
  <Link href={cta.href}>
    <Button variant="contained" color="primary" size="large">
      {cta.label}
    </Button>
  </Link>
);

const DefaultSecondaryButton = ({ cta }) => (
  <Link href={cta.href}>
    <Button variant="outlined" color="primary" size="large">
      {cta.label}
    </Button>
  </Link>
);

const renderPrimary = (primaryCta, isContrast, palette) => {
  if (!primaryCta) return null;
  if (isContrast)
    return <ContrastPrimaryButton cta={primaryCta} palette={palette} />;
  return <DefaultPrimaryButton cta={primaryCta} />;
};

const renderSecondary = (secondaryCta, isContrast, palette) => {
  if (!secondaryCta) return null;
  if (isContrast)
    return <ContrastSecondaryButton cta={secondaryCta} palette={palette} />;
  return <DefaultSecondaryButton cta={secondaryCta} />;
};

const FinalCTA = ({
  headline,
  body,
  primaryCta,
  secondaryCta,
  tertiaryLink,
  variant = "default",
  visual
}) => {
  const { palette } = useTheme();
  const isContrast = variant === "contrast";

  return (
    <Box
      sx={{
        py: { xs: 6, md: 10 },
        bgcolor: isContrast ? palette.primary.main : palette.background.paper,
        color: isContrast ? palette.background.paper : "inherit",
        position: "relative"
      }}
    >
      <Container maxWidth="lg">
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: visual ? { xs: "1fr", md: "1fr 1fr" } : "1fr",
            gap: { xs: 4, md: 6 },
            alignItems: "center",
            textAlign: visual ? "left" : "center"
          }}
        >
          <Box>
            <Box mb={2}>
              <Typography
                variant="h2"
                color={isContrast ? "inherit" : "textPrimary"}
              >
                {headline}
              </Typography>
            </Box>
            {body && (
              <Box
                mb={3}
                sx={{
                  opacity: isContrast ? 0.9 : 1,
                  maxWidth: visual ? "none" : 720,
                  mx: visual ? 0 : "auto"
                }}
              >
                <Typography
                  variant="body1"
                  color={isContrast ? "inherit" : "textSecondary"}
                >
                  {body}
                </Typography>
              </Box>
            )}
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1.5}
              justifyContent={visual ? "flex-start" : "center"}
              sx={{ mb: tertiaryLink ? 2 : 0 }}
            >
              {renderPrimary(primaryCta, isContrast, palette)}
              {renderSecondary(secondaryCta, isContrast, palette)}
            </Stack>
            {tertiaryLink && (
              <Link href={tertiaryLink.href}>
                <Box
                  display="inline-flex"
                  alignItems="center"
                  sx={{ gap: 0.75 }}
                >
                  <Typography
                    variant="body2"
                    color={isContrast ? "inherit" : "primary"}
                  >
                    {tertiaryLink.label}
                  </Typography>
                  <FontAwesomeIcon
                    icon={faArrowRight}
                    style={{
                      color: isContrast
                        ? palette.background.paper
                        : palette.primary.main,
                      fontSize: 12
                    }}
                  />
                </Box>
              </Link>
            )}
          </Box>
          {visual && (
            <Box display="flex" alignItems="center" justifyContent="center">
              {visual}
            </Box>
          )}
        </Box>
      </Container>
    </Box>
  );
};
export default FinalCTA;
